import React from 'react';
import { Link } from 'react-router-dom';

export default function FathomPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-zinc-400 text-lg">Fathom - Scuba Dive Logbook</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: July 21, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p>
                Welcome to Fathom ("we," "our," or "us"). Fathom is a local-first dive logbook
                for logging your dives, tracking your certifications, and building a life list
                of the species you've seen. This Privacy Policy explains how information is
                handled when you use our mobile application ("App"). The short version: your
                logbook lives on your device, and we like it that way.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="mb-4">
                <strong className="text-white">We do not collect any personal information.</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We do not require you to create an account</li>
                <li>We do not collect your name, email address, or any contact information</li>
                <li>We do not collect payment information (purchases are handled entirely by the App Store)</li>
                <li>We do not track your location — dive sites are whatever you type or pick, never GPS</li>
                <li>We do not use cookies or similar tracking technologies</li>
                <li>We do not collect usage analytics</li>
              </ul>
            </section>

            {/* Your Data Stays on Your Device */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Logbook Stays on Your Device</h2>
              <p>
                Everything you create in Fathom — your dives, dive sites, species sightings,
                buddies, certifications, notes, photos, and instructor signatures — is stored
                locally on your device in the App's private database. We do not have access to
                this data, and it is never transmitted to our servers (we don't have any). The
                App works fully offline. If you delete the App, this data is removed from your
                device, so we recommend using the built-in JSON export (Settings → Your
                logbook) to keep your own backup.
              </p>
            </section>

            {/* Purchases */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Purchases</h2>
              <p>
                Fathom Premium is a one-time purchase processed by Apple through the App Store.
                We never see your payment details. To validate the purchase and support
                restoring it on a new device, the App uses RevenueCat, a purchase
                infrastructure service. RevenueCat receives the App Store purchase receipt and
                a random, anonymous identifier generated by the App — never your name, your
                logbook, or anything else about you. You can read RevenueCat's privacy policy
                at{' '}
                <a
                  href="https://www.revenuecat.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  revenuecat.com/privacy
                </a>. This purchase check is the only network communication in the App.
              </p>
            </section>

            {/* Photos */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Photos</h2>
              <p>
                If you choose to attach photos to a dive or a certification card, the App asks
                for access to your photo library. Selected photos are resized and copied into
                the App's local storage on your device only. Photos are never uploaded
                anywhere.
              </p>
            </section>

            {/* Signatures */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Instructor Signatures</h2>
              <p>
                When a divemaster or instructor signs one of your dives, their signature,
                name, agency, and the date are stored on your device as part of that dive
                entry, exactly like the rest of your logbook. Signatures appear in the App and
                in PDF logbooks you choose to export. They are never transmitted to us.
              </p>
            </section>

            {/* Data Export */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Export</h2>
              <p>
                You can export your full logbook as a JSON file, and selected dives as a PDF,
                at any time. Exports are created on your device and shared only where you
                choose to send them (e.g., your own cloud storage, email, or a dive shop).
                Once you share an export, its privacy is governed by the service you shared it
                to.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
              <p>
                Fathom does not collect personal information from anyone, including children
                under the age of 13.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make changes, we
                will notify you by revising the "Last updated" date at the top of this policy.
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us at:
              </p>
              <p className="mt-4">
                <a
                  href="mailto:parkerdevwork@gmail.com"
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>
              </p>
            </section>

            {/* Summary */}
            <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-6 mt-12">
              <h2 className="text-xl font-semibold text-white mb-4">Summary</h2>
              <p className="text-zinc-400">
                In short: We don't collect your data. We don't require accounts. We don't
                track you. Your dives, photos, certifications, and signatures live only on
                your device. The only thing that ever touches the network is the App Store
                purchase check for Premium — and that carries an anonymous identifier, not
                you.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Fathom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
